import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import classNames from 'classnames';
import { CookieConsent, COOKIE_CATEGORIES } from '@/types/cookies';
import { SVGClose } from '@/images/svg/svg-icons';
import { trackCookieConsent } from '@/utils/tracking';

interface CookiePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (consent: Omit<CookieConsent, 'timestamp' | 'version'>) => void;
  currentConsent?: CookieConsent | null;
}

const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentConsent
}) => {
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });

  // Initialize preferences from current consent
  useEffect(() => {
    if (currentConsent) {
      setPreferences({
        essential: currentConsent.essential,
        analytics: currentConsent.analytics,
        marketing: currentConsent.marketing
      });
    }
  }, [currentConsent]);

  const handleToggle = (category: keyof typeof preferences) => {
    if (category === 'essential') return;
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    onSave(preferences);
    trackCookieConsent('customize', preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    onSave(allAccepted);
    trackCookieConsent('accept_all', allAccepted);
    onClose();
  };

  const handleRejectAll = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false
    };
    setPreferences(essentialOnly);
    onSave(essentialOnly);
    trackCookieConsent('reject_all', essentialOnly);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal-content mt-4"
      overlayClassName="modal-overlay"
      closeTimeoutMS={100}
      
    >
      <div className="content md:w-[640px] w-full  overflow-auto">
        <div className="close" onClick={onClose}>
          <SVGClose />
        </div>
        <div className='px-6 py-4 border-b border-main-gray'>
             <h2 className="text-main-blue text-left text-2xl  font-medium text-main-black mb-0">
            Cookie Preferences
          </h2>

        </div>
        <div className="p-6 ">

         
          <p className="text-main-neutrals text-base mb-4">
            Manage your cookie preferences below. You can enable or disable different types of cookies. 
            Essential cookies cannot be disabled as they are necessary for the website to function properly.
          </p>
          
          <div className="space-y-4">
            {COOKIE_CATEGORIES.map((category) => (
              <div key={category.id} className="border border-main-gray rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-main-black mb-1">
                      {category.name}
                    </h3>
                    <p className="text-main-neutrals text-sm leading-relaxed">
                      {category.description}
                    </p>
                  </div>
                  
                  <div className="ml-4">
                    {category.required ? (
                      <div className="px-3 py-1 bg-main-gray text-main-neutrals text-xs rounded">
                        Required
                      </div>
                    ) : (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences[category.id]}
                          onChange={() => handleToggle(category.id)}
                          className="sr-only"
                        />
                        <div className={classNames(
                          'w-11 h-6 rounded-full transition-colors',
                          preferences[category.id] ? 'bg-main-blue' : 'bg-main-gray'
                        )}>
                          <div className={classNames(
                            'w-5 h-5 bg-white rounded-full shadow-md transform transition-transform',
                            'absolute top-0.5',
                            preferences[category.id] ? 'translate-x-5' : 'translate-x-0.5'
                          )}></div>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
                
                {/* {category.cookies.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-main-gray">
                    <p className="text-xs text-main-neutrals mb-1">Cookies used:</p>
                    <div className="flex flex-wrap gap-1">
                      {category.cookies.map((cookie) => (
                        <span
                          key={cookie}
                          className="px-2 py-1 bg-main-tab text-main-neutrals text-xs rounded"
                        >
                          {cookie}
                        </span>
                      ))}
                    </div>
                  </div>
                )} */}
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 mt-4 pt-4 border-t border-main-gray">
            <button
              onClick={handleRejectAll}
              className="flex-1 px-4 py-3 text-main-neutrals border border-main-gray rounded hover:bg-main-gray transition-colors font-medium"
            >
              Reject All
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-4 py-3 text-main-blue border border-main-blue rounded hover:bg-main-blue hover:text-white transition-colors font-medium"
            >
              Accept All
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-3 bg-main-blue text-white rounded hover:bg-opacity-90 transition-colors font-medium"
            >
              Save Preferences
            </button>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-xs text-main-neutrals">
              For more information, please read our{' '}
              <a 
                href="/privacy-policy" 
                className="text-main-blue hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a 
                href="/terms-and-conditions" 
                className="text-main-blue hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms & Conditions
              </a>.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CookiePreferencesModal;
